
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Rocket, 
  GraduationCap, 
  Megaphone, 
  Shield,
  Settings,
  ArrowRight,
  Download,
  FileText,
  Video
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const HQKits = () => {
  const navigate = useNavigate();

  const kits = [
    {
      id: 'launch',
      title: "Launch Kit",
      description: "Everything you need for successful studio launches",
      icon: Rocket,
      path: "/hq/kits/launch",
      color: "amber",
      files: 15,
      categories: ["Business Plan", "Marketing", "Setup Guides", "Checklists"]
    },
    {
      id: 'training',
      title: "Training Kit", 
      description: "Staff training modules and certification resources",
      icon: GraduationCap,
      path: "/hq/kits/training",
      color: "blue",
      files: 22,
      categories: ["Video Training", "Assessments", "Certifications", "SOPs"]
    },
    {
      id: 'marketing',
      title: "Marketing Kit",
      description: "Brand assets, templates, and campaign materials",
      icon: Megaphone,
      path: "/hq/kits/marketing",
      color: "purple",
      files: 18,
      categories: ["Brand Assets", "Social Templates", "Print Materials", "Campaigns"]
    },
    {
      id: 'compliance',
      title: "Compliance Kit",
      description: "Legal documents, forms, and regulatory guidelines",
      icon: Shield,
      path: "/hq/kits/compliance",
      color: "green",
      files: 12,
      categories: ["Legal Forms", "Consent Documents", "Compliance Guides", "Contracts"]
    },
    {
      id: 'technology',
      title: "Technology Kit",
      description: "Software setup, integrations, and technical resources",
      icon: Settings,
      path: "/hq/kits/technology",
      color: "indigo",
      files: 9,
      categories: ["Setup Guides", "Integrations", "Troubleshooting", "API Docs"]
    }
  ];

  const getColorClasses = (color: string) => {
    const colorMap = {
      amber: {
        bg: 'bg-amber-500/20',
        text: 'text-amber-400',
        border: 'border-amber-500/30',
        hover: 'hover:bg-amber-500/30'
      },
      blue: {
        bg: 'bg-blue-500/20',
        text: 'text-blue-400',
        border: 'border-blue-500/30',
        hover: 'hover:bg-blue-500/30'
      },
      purple: {
        bg: 'bg-purple-500/20',
        text: 'text-purple-400',
        border: 'border-purple-500/30',
        hover: 'hover:bg-purple-500/30'
      },
      green: {
        bg: 'bg-emerald-500/20',
        text: 'text-emerald-400',
        border: 'border-emerald-500/30',
        hover: 'hover:bg-emerald-500/30'
      },
      indigo: {
        bg: 'bg-indigo-500/20',
        text: 'text-indigo-400',
        border: 'border-indigo-500/30',
        hover: 'hover:bg-indigo-500/30'
      }
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.amber;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">
                JUV Kit Library
              </h1>
              <p className="mt-1 text-amber-200">
                Launch, Train, and Operate with Confidence
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Welcome Section */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-white mb-4">Welcome to the JUV Kit Library</h2>
              <p className="text-slate-300 text-lg leading-relaxed">
                Every toolkit below is designed to help you launch and operate your studio efficiently—from training new staff to executing a successful grand opening. Each kit includes downloads, templates, video walkthroughs, and SOPs specific to your role.
              </p>
            </CardContent>
          </Card>

          {/* Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-slate-800/50 backdrop-blur border-amber-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-amber-500/20">
                    <FileText className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-200">Total Resources</p>
                    <p className="text-2xl font-bold text-white">76</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-blue-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-blue-500/20">
                    <Video className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-200">Training Videos</p>
                    <p className="text-2xl font-bold text-white">24</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-emerald-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-emerald-500/20">
                    <Download className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-200">Downloads</p>
                    <p className="text-2xl font-bold text-white">1,247</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Available Kits */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Available Kits</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {kits.map((kit) => {
                const colors = getColorClasses(kit.color);
                const IconComponent = kit.icon;
                
                return (
                  <Card 
                    key={kit.id} 
                    className={`bg-slate-800/50 backdrop-blur border-slate-600/30 ${colors.hover} transition-all duration-300 cursor-pointer group`}
                    onClick={() => navigate(kit.path)}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colors.bg}`}>
                            <IconComponent className={`h-6 w-6 ${colors.text}`} />
                          </div>
                          <div>
                            <CardTitle className="text-white text-lg">{kit.title}</CardTitle>
                            <p className="text-slate-300 text-sm mt-1">{kit.description}</p>
                          </div>
                        </div>
                        <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-white transition-colors" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-400">Resources</span>
                          <Badge className={`${colors.bg} ${colors.text} ${colors.border}`}>
                            {kit.files} files
                          </Badge>
                        </div>
                        
                        <div>
                          <p className="text-slate-400 text-sm mb-2">Includes:</p>
                          <div className="flex flex-wrap gap-1">
                            {kit.categories.map((category, index) => (
                              <Badge key={index} className="bg-slate-700/50 text-slate-300 border-slate-600/50 text-xs">
                                {category}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        
                        <Button 
                          className={`w-full ${colors.bg} ${colors.text} border ${colors.border} hover:bg-opacity-80`}
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(kit.path);
                          }}
                        >
                          Access {kit.title}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HQKits;
